/**
 * Monta o paciente da evolução a partir do id do prontuário (CPF/vet)
 * ou de um Patient FHIR já carregado.
 */
import { currentFeatures, oids } from "@/configs/Settings";
import { GetPatient } from "@/services/fhir";
import { GetVetPatient } from "@/services/fhir/vetpatient/GetVetPatient";
import { useFormatter } from "@/utils/useFormatter";

export interface EvolutionPatient {
  reference: string;
  display: string;
  cpf: string;
  responsibleCpf?: string;
}

/** Id FHIR do Patient usado em GetAppointment(actor=Patient/{id}). */
export function toPatientResourceId(patientId: string): string {
  if (!patientId) return "";
  if (!currentFeatures.defaultApp) return patientId;
  if (patientId.includes(oids.cpf)) return patientId;
  const digits = patientId.replace(/[^a-zA-Z0-9]/g, "");
  return `${oids.cpf}-${digits}`;
}

export function toEvolutionPatient(resource: fhir5.Patient): EvolutionPatient {
  const id = resource.id || "";
  const cpfFromId =
    currentFeatures.defaultApp && id.includes("-")
      ? id.split("-").slice(1).join("-")
      : "";
  const cpfFromIdentifier = (resource.identifier?.[0]?.value || "").replace(
    /\D/g,
    "",
  );
  const name =
    resource.name?.find((n) => n.use === "official" || n.use === "usual")
      ?.text ||
    resource.name?.[0]?.text ||
    "";

  return {
    reference: `Patient/${id}`,
    display: useFormatter.formatNameDisplay(name),
    cpf: (cpfFromId || cpfFromIdentifier).replace(/\D/g, ""),
  };
}

/** Busca Patient no MPI (humano) ou VetPatient (vet) a partir do id do sumário. */
export async function fetchEvolutionPatient(
  patientId: string,
): Promise<EvolutionPatient | null> {
  if (!patientId) return null;

  try {
    if (currentFeatures.defaultApp) {
      const digits = patientId.replace(/[^a-zA-Z0-9]/g, "");
      const response = await GetPatient({ identifier: digits });
      const resource = response?.data as fhir5.Patient | undefined;
      if (resource?.id) return toEvolutionPatient(resource);
      return {
        reference: `Patient/${oids.cpf}-${digits}`,
        display: "",
        cpf: digits,
      };
    }

    const response = await GetVetPatient({ id: patientId });
    const resource = response?.data as fhir5.Patient | undefined;
    if (resource?.id) return toEvolutionPatient(resource);
    return {
      reference: `Patient/${patientId}`,
      display: "",
      cpf: "",
    };
  } catch (error) {
    console.error("fetchEvolutionPatient", error);
    return null;
  }
}
